import { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import type { IScheduleProfession } from '@interfaces/IScheduler';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { tradeLabel } from '@utils/tradeLabel';
import type { IPageFilters } from '../pageFilters';
import type { IListItem } from '../model/listItem';
import { usePlanFeed } from './usePlanFeed';
import {
  groupRows,
  hidesEveryRung,
  issueLane,
  placeRow,
  promiseFacts,
  summaryFor,
  visibleRows,
  zoneFloors,
  type IPlanWindow,
  type PlanGroupBy,
} from './workPlanModel';
import { readWorkPlanPrefs, writeWorkPlanPrefs } from './workPlanPrefs';

export interface IUseWorkPlanArgs {
  siteId: string;
  /** The page's unified items, UNFILTERED — the view applies the panel itself. */
  items: IListItem[];
  filters: IPageFilters;
  /** The site's today. */
  today: string;
  /** The page's date window (its date picker) — what the grid draws. */
  window: IPlanWindow;
}

/**
 * The Work Plan's state: the panel's filters, the page's date window and the
 * view's own grouping over the page's feed, through the pure model. Memoised
 * in two stages like `useUnifiedList`: the feed rebuilds only when the data or
 * the panel changes; a grouping change re-lays the grid at transition priority
 * (the tracker's `startRelayout`).
 */
export function useWorkPlan({ siteId, items, filters, today, window }: IUseWorkPlanArgs) {
  const { t } = useTranslation();
  const { floors } = useSiteLocations(siteId);
  const [prefs, setPrefs] = useState(() => readWorkPlanPrefs(siteId));
  const [, startRelayout] = useTransition();
  useEffect(() => {
    writeWorkPlanPrefs(siteId, prefs);
  }, [siteId, prefs]);

  // Stage 1 — the panel narrows the feed (`usePlanFeed`), the issues under
  // their Status checkboxes already.
  const { rows, issues } = usePlanFeed(items, filters, today);

  // Stage 2 — what the window shows, the rows under the rung checkboxes.
  // Every rung unchecked takes the rows away; no issue left, the lane.
  const hidden = filters.hiddenStatuses;
  const professionLabel = useCallback((profession: IScheduleProfession) => tradeLabel(t, profession.tradeId), [t]);
  const visible = useMemo(() => visibleRows(rows, window, hidden), [rows, window, hidden]);
  const groups = useMemo(() => {
    const floorOfZone = zoneFloors(floors);
    const placed = visible.map((row) => ({
      ...row,
      placement: placeRow(row, window),
      facts: promiseFacts(row.task, floors, floorOfZone),
    }));
    return groupRows(placed, prefs.groupBy, { floors, professionLabel });
  }, [visible, window, prefs.groupBy, floors, professionLabel]);
  const lane = useMemo(() => (issues.length > 0 ? issueLane(issues, window) : null), [issues, window]);
  const summary = useMemo(() => summaryFor(rows, window, issues, today), [rows, window, issues, today]);

  const controls = {
    groupBy: prefs.groupBy,
    setGroupBy: (groupBy: PlanGroupBy) => startRelayout(() => setPrefs({ groupBy })),
  };

  return { groups: hidesEveryRung(hidden) ? null : groups, lane, summary, controls };
}

export type WorkPlanState = ReturnType<typeof useWorkPlan>;
export type WorkPlanControls = WorkPlanState['controls'];
